'use client';

import { useEffect, useState } from 'react';
import { Map, Grid3x3, List, ArrowLeft, Store } from 'lucide-react';
import Link from 'next/link';
import DirectorySearch from '@/components/directory/DirectorySearch';
import DirectoryGrid from '@/components/directory/DirectoryGrid';
import DirectoryList from '@/components/directory/DirectoryList';
import { DirectoryFilters } from '@/components/directory/DirectoryFilters';
import { usePlatformSettings } from '@/contexts/PlatformSettingsContext';
import dynamic from 'next/dynamic';
import { trackBehaviorClient } from '@/utils/behaviorTracking';
import { useDirectoryShelfTracking } from '@/hooks/useDirectoryShelfTracking';
import { PoweredByFooter } from '@/components/PoweredByFooter';
import { MarketIntelBanner } from '@/components/place/MarketIntelBanner';
import { recommendationsService } from '@/services/RecommendationsSingletonService';
import { clientLogger } from '@/lib/client-logger';

// Dynamically import Google Maps to avoid SSR issues
const DirectoryMapGoogle = dynamic(() => import('@/components/directory/DirectoryMapGoogle'), {
  ssr: false,
  loading: () => <div className="w-full h-[600px] bg-gray-100 rounded-lg flex items-center justify-center">Loading map...</div>
});

interface DirectoryListing {
  id: string;
  tenantId: string;
  businessName: string;
  slug: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  logoUrl?: string;
  primaryCategory?: string;
  ratingAvg: number;
  ratingCount: number;
  productCount: number;
  isFeatured: boolean;
  subscriptionTier: string;
  useCustomWebsite: boolean;
  website?: string;
  distance?: number;
  isOpen?: boolean;
}

interface DirectoryResponse {
  listings: DirectoryListing[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

interface StoreType {
  name: string;
  slug: string;
  storeCount: number;
  productCount?: number;
}

interface StoreTypeViewClientProps {
  storeTypeSlug: string;
  searchParams: {
    lat?: string;
    lng?: string;
    radius?: string;
    search?: string;
  };
}

export default function StoreTypeViewClient({
  storeTypeSlug,
  searchParams,
}: StoreTypeViewClientProps) {
  const { settings } = usePlatformSettings();
  const [data, setData] = useState<DirectoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [storeType, setStoreType] = useState<StoreType | null>(null);
  
  // Persist view mode in localStorage - start with default to avoid hydration mismatch
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');

  // Layer 3 — shelf engagement for the store-type shelf surface.
  useDirectoryShelfTracking({
    surface: 'directory_store_type',
    ref: storeTypeSlug,
    filterSignature: `${viewMode}|${searchParams.lat ?? ''}|${searchParams.lng ?? ''}|${searchParams.radius ?? ''}`,
  });

  // Load saved view mode after hydration
  useEffect(() => {
    const saved = localStorage.getItem('directory-view-mode');
    if (saved && ['grid', 'list', 'map'].includes(saved)) {
      setViewMode(saved as 'grid' | 'list' | 'map');
    }
  }, []);

  // Save view mode to localStorage when it changes
  const handleViewModeChange = (mode: 'grid' | 'list' | 'map') => {
    setViewMode(mode);
    localStorage.setItem('directory-view-mode', mode);
  };

  // Track store type page view
  useEffect(() => {
    // Track store type browsing when component mounts
    const formatStoreTypeName = (slug: string) => {
      return slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    trackBehaviorClient({
      entityType: 'category', // Use category for store types (they're similar)
      entityId: storeTypeSlug,
      entityName: formatStoreTypeName(storeTypeSlug),
      context: {
        store_type_slug: storeTypeSlug,
        store_type_name: formatStoreTypeName(storeTypeSlug),
        search_params: searchParams,
        view_mode: viewMode
      },
      pageType: 'directory_store_type'
    });
  }, [storeTypeSlug]); // Only track once when store type changes

  // Track view mode changes
  useEffect(() => {
    if (data) { // Only track after data is loaded
      trackBehaviorClient({
        entityType: 'category',
        entityId: storeTypeSlug,
        context: {
          store_type_slug: storeTypeSlug,
          action: 'view_mode_change',
          view_mode: viewMode,
          stores_count: data.pagination.totalItems
        },
        pageType: 'directory_store_type'
      });
    }
  }, [viewMode, storeTypeSlug, data]);

  // Fetch store type info and stores
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch store type details
        const typeData = await recommendationsService.getStoreTypeDetails(storeTypeSlug);
        if (typeData) {
          setStoreType(typeData.data?.storeType || null);
        }

        // 2. Fetch stores of this type using store-types endpoint
        const storesData = await recommendationsService.getStoresByStoreType(storeTypeSlug);

        if (!storesData) {
          throw new Error('Failed to fetch stores');
        }

        // 3. Transform to DirectoryResponse format
        const stores = storesData.data?.stores || [];
        setData({
          listings: stores.map((store: any) => ({
            id: store.id,
            tenantId: store.id,
            businessName: store.name,
            slug: store.slug,
            address: store.address,
            city: store.city,
            state: store.state,
            zipCode: store.postalCode,
            latitude: store.latitude,
            longitude: store.longitude,
            productCount: store.productCount || 0,
            primaryCategory: store.primaryCategory,
            gbpPrimaryCategoryName: store.gbpCategoryName,
            logoUrl: store.logoUrl,
            description: store.description,
            ratingAvg: store.ratingAvg || 0,
            ratingCount: store.ratingCount || 0,
            isFeatured: store.isFeatured || false,
            subscriptionTier: store.subscriptionTier || 'trial',
            useCustomWebsite: store.useCustomWebsite || false,
            businessHours: store.businessHours,
          })),
          pagination: {
            page: 1,
            limit: stores.length,
            totalItems: storesData.data?.totalCount || stores.length,
            totalPages: 1,
          },
        });
      } catch (err) {
        clientLogger.error('Error fetching store type data:', { detail: err });
        setError('Failed to load store type. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [storeTypeSlug, searchParams.lat, searchParams.lng, searchParams.radius]);

  const currentPage = data?.pagination.page || 1;
  const totalPages = data?.pagination.totalPages || 1;
  const totalItems = data?.pagination.totalItems || 0;

  // Convert slug to display name
  const displayName = storeType?.name || storeTypeSlug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Page Title Section */}
      <div className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <div className="container mx-auto px-4 py-6">
          {/* Breadcrumb */}
          <nav className="mb-3 text-sm flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
            <Link href="/directory" className="hover:text-green-600 dark:hover:text-green-400">
              Directory
            </Link>
            <span>›</span>
            <span className="font-semibold text-neutral-900 dark:text-white">{displayName}</span>
          </nav>

          {/* Title */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">{displayName}</h1>
              <span className="text-2xl">🏪</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                href="/directory/stores"
                className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
              >
                <Store className="w-4 h-4" />
                <span className="hidden sm:inline">All Store Types</span>
              </Link>
              <Link
                href="/directory"
                className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Link>
            </div>
          </div>

          <p className="text-neutral-600 dark:text-neutral-400 mt-2">
            {totalItems} {totalItems === 1 ? 'store' : 'stores'}
            {storeType?.productCount && ` · ${storeType.productCount} products`}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="container mx-auto px-4 py-4">
          <DirectorySearch />
        </div>
      </div>

      {/* Filters - Store type is locked */}
      <DirectoryFilters
        categories={[]} // Can add product category filters here
        locations={[]}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Results Header with View Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-neutral-600 dark:text-neutral-400">
              {loading ? 'Loading...' : `Showing ${totalItems} ${totalItems === 1 ? 'store' : 'stores'}`}
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => handleViewModeChange('grid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => handleViewModeChange('map')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'map'
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              }`}
            >
              <Map className="w-4 h-4" />
              <span className="hidden sm:inline">Map</span>
            </button>
          </div>
        </div>

        {/* Listings + tall-banner rail. The rail holds the 300x600 report
            slot (sticky on desktop); no store-type teaser exists, so the
            creative falls back to house copy. */}
        <div className="lg:flex lg:gap-8">
          <div className="min-w-0 flex-1">
        {/* Views - REUSE EXACT COMPONENTS */}
        {viewMode === 'grid' && (
          <DirectoryGrid
            listings={data?.listings || []}
            loading={loading}
            pagination={data?.pagination}
            categorySlug={storeTypeSlug}
            shelfRef={`directory/store-type/${storeTypeSlug}`}
          />
        )}

        {viewMode === 'list' && (
          <DirectoryList
            listings={data?.listings || []}
            loading={loading}
            shelfRef={`directory/store-type/${storeTypeSlug}`}
          />
        )}

        {viewMode === 'map' && (
          <DirectoryMapGoogle
            listings={data?.listings || []}
            useMapEndpoint={false} // Use the listings directly since map endpoint doesn't support store type filtering
            filters={{}}
            shelfRef={`directory/store-type/${storeTypeSlug}`}
          />
        )}

        {/* Store Type Recommendations */}
        <StoreTypeRecommendations storeTypeSlug={storeTypeSlug} />
          </div>

          <aside className="mt-8 shrink-0 lg:mt-0 lg:w-[336px]">
            <div className="lg:sticky lg:top-6">
              <MarketIntelBanner variant="tall" surfaceType="directory" />
            </div>
          </aside>
        </div>

        {/* Square banner slot (300x250) — in-flow, end of the listings. */}
        {totalItems > 0 && (
          <div className="mt-10 flex justify-center">
            <MarketIntelBanner variant="square" surfaceType="directory" />
          </div>
        )}
      </div>

                  {/* Platform Branding Footer */}
                  <PoweredByFooter />
    </div>
  );
}

// Store Type Recommendations Component
function StoreTypeRecommendations({ storeTypeSlug }: { storeTypeSlug: string }) {
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const data = await recommendationsService.getStoreTypeRecommendations(storeTypeSlug);
        setRecommendations(data?.recommendations || []);
      } catch (error) {
        clientLogger.error('Error fetching store type recommendations:', { detail: error });
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [storeTypeSlug]);

  if (loading || recommendations.length === 0) {
    return null;
  }

  return (
    <div className="mt-12 border-t border-neutral-200 dark:border-neutral-800 pt-8">
      <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">
        Recommended For You
      </h2>
      <div className="grid gap-4">
        {recommendations.map((rec, index) => (
          <Link
            key={rec.tenantId}
            href={`/directory/${rec.slug}?source=recommendation`}
            className="flex items-center justify-between p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-green-500 dark:hover:border-green-400 transition-colors"
          >
            <div className="flex-1">
              <h3 className="font-medium text-neutral-900 dark:text-white">
                {rec.businessName}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {rec.address}
              </p>
              <p className="text-sm text-neutral-500">
                {rec.city}, {rec.state}
                {rec.distance && ` • ${rec.distance} mi`}
              </p>
              <p className="text-xs text-green-600 mt-2 font-medium">
                {rec.reason}
              </p>
            </div>
            <div className="ml-3 shrink-0">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
